import { useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import clsx from "clsx";
import { Avatar, Button, Checkbox, Select, SelectItem, Spinner, Tab, Tabs } from "@heroui/react";
import { ArrowTopRightOnSquareIcon, DocumentArrowDownIcon, LinkIcon } from "@heroicons/react/24/outline";
import { buildSourceChain } from "./utils/sourceChainWalker";
import { Icon } from "@iconify/react/dist/iconify.js";

import useDataEvent from "./CustomHook";
import MainStore, { uniquePrimitives } from "./MainStore";
import { PrimitiveCard } from "./PrimitiveCard";
import FeedList from "./@components/Feed";
import FlowInstanceOutput from "./FlowInstanceOutput";
import WorkflowStructurePreview from "./WorkflowStructurePreview";
import PrimitiveConfig from "./PrimitiveConfig";
import GanttChart from "./GanttChart";
import { FlowContent } from "./sidebar/FlowContent";
import WorkflowInstanceStatus from "./@components/WorkflowInstanceStatus";
import TooggleButton from "./ToggleButton";
import useWorkspaceBranding from "./hooks/useWorkspaceBranding";
import { VFImage } from "./VFImage";
import {
    AppMenuButton,
    CreditsBadge,
    UserMenuButton,
    useApplicationChrome,
} from "./ApplicationLayout.jsx";

function normalizeConfigurationSelection(info = {}, rawValue) {
    const optionIds = Array.isArray(info?.options)
        ? info.options.map((option) => option?.id).filter(Boolean)
        : []
    const optionIdSet = new Set(optionIds)
    const filtered = [rawValue]
        .flat()
        .map((value) => `${value ?? ""}`.trim())
        .filter((value) => value.length > 0 && optionIdSet.has(value))

    if (info?.can_select_multiple === true) {
        if (filtered.length > 0) {
            return Array.from(new Set(filtered))
        }
        const defaults = [info?.defaultValues]
            .flat()
            .map((value) => `${value ?? ""}`.trim())
            .filter((value) => value.length > 0 && optionIdSet.has(value))
        if (defaults.length > 0) {
            return Array.from(new Set(defaults))
        }
        const fallback = `${info?.default ?? ""}`.trim()
        return fallback && optionIdSet.has(fallback) ? [fallback] : []
    }

    if (filtered.length > 0) {
        return filtered[0]
    }
    const fallback = `${info?.default ?? ""}`.trim()
    if (fallback && optionIdSet.has(fallback)) {
        return fallback
    }
    const multiFallback = [info?.defaultValues]
        .flat()
        .map((value) => `${value ?? ""}`.trim())
        .find((value) => value.length > 0 && optionIdSet.has(value))
    return multiFallback || ""
}

function galleryImageKey(image, idx) {
    const primitiveId = image?.primitiveId ?? "none";
    const url = typeof image?.url === "string" ? image.url : "";
    return `${primitiveId}|${url}|${idx}`;
}

export default function FlowInstancePage({ primitive }) {
    const navigate = useNavigate()        
    const { id } = useParams();
    const activeWorkspaceId = MainStore().activeWorkspaceId
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const isEmbedded = query.get("embed");
    const { setAppHeaderVisible } = useApplicationChrome();

    const flowInfoRef = useRef(null);
    const thumbnailsRef = useRef(null);
    const [update, forceUpdate] = useReducer((x) => x + 1, 0);
    const [dataForNewInstance, setDataForNewInstance] = useState({});
    const [missing, setMissing] = useState({});
    const [statusMap, setStatusMap] = useState({});
    const [stepsToProcess, setStepsToProcess] = useState([]);
    const [timelineData, setTimelineData] = useState([]);
    const [inspectorFocus, setInspectorFocus] = useState(undefined);
    const [gallerySelectionByKey, setGallerySelectionByKey] = useState({});
    const [completeView, setCompleteView] = useState("output");
    const [selectedPage, setSelectedPage] = useState(0);
    const [isWorkspaceLoading, setIsWorkspaceLoading] = useState(false);
    const [hideSlidesWithEmptyItems, setHideSlidesWithEmptyItems] = useState(true);
    const [dataLoadVersion, setDataLoadVersion] = useState(0);
    const loadingFlowInstanceIdRef = useRef(null);
    // Viz provenance panel state
    const [vizExpandedQuotes, setVizExpandedQuotes] = useState({});
    const [vizSearchTerm, setVizSearchTerm] = useState("");
    const [vizSourcePage, setVizSourcePage] = useState(0);
    const [vizPageSize, setVizPageSize] = useState(20);
    const [vizRetry, setVizRetry] = useState(0);
    const vizMissingRef = useRef([]);

    useEffect(() => {
        if (!inspectorFocus?.vizProvenance) {
            setVizSearchTerm("");
            setVizExpandedQuotes({});
            setVizSourcePage(0);
        }
    }, [inspectorFocus]);
    useEffect(() => {
        const ids = vizMissingRef.current;
        if (ids.length === 0) return;
        vizMissingRef.current = [];
        MainStore().fetchPrimitivesBatch(ids).then(() => setVizRetry(c => c + 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inspectorFocus, vizRetry]);
    const mainstore = MainStore()
    const isExternalUser = Boolean(mainstore.activeUser?.external || mainstore.activeUser?.info?.external)
    const loadDebugEnabled = () => {
        try {
            return window?.__DEBUG_PRIMITIVE_LOADS__ === true || window?.localStorage?.getItem("debug:primitive-loads") === "1";
        } catch (error) {
            return false;
        }
    };
    const logLoadDebug = (event, payload = {}) => {
        if (!loadDebugEnabled()) {
            return;
        }
        console.log(`[flowinstance-page] ${new Date().toISOString()} ${event}`, payload);
    };

    let resolvedPrimitiveRaw = primitive;
    if (!resolvedPrimitiveRaw && id) {
        resolvedPrimitiveRaw = MainStore().primitive(id);
    }
    const [stablePrimitive, setStablePrimitive] = useState(undefined);

    useEffect(() => {
        if (resolvedPrimitiveRaw) {
            setStablePrimitive(resolvedPrimitiveRaw);
        }
    }, [resolvedPrimitiveRaw?.id, resolvedPrimitiveRaw?.type, resolvedPrimitiveRaw?.workspaceId]);

    const resolvedPrimitive =
        resolvedPrimitiveRaw ?? (isWorkspaceLoading ? stablePrimitive : undefined);

    useEffect(() => {
        let cancelled = false;
        const routeFlowInstanceId = id ? `${id}` : undefined;
        const store = MainStore();
        const inFlowInstanceRoute = location.pathname.startsWith("/flowinstance/");

        const finishLoad = () => {
            if (loadingFlowInstanceIdRef.current === routeFlowInstanceId) {
                loadingFlowInstanceIdRef.current = null;
            }
            if (!cancelled) {
                setIsWorkspaceLoading(false);
                setDataLoadVersion((prev) => prev + 1);
                setSelectedPage(0);
            }
        };

        const loadFlowData = async () => {
            logLoadDebug("load_effect:start", {
                routeFlowInstanceId,
                path: location.pathname,
                loadingRef: loadingFlowInstanceIdRef.current,
                loadedWorkspaceMode: store.loadedWorkspaceMode,
                loadedWorkspaceFocusId: store.loadedWorkspaceFocusId
            });

            if (!routeFlowInstanceId) {
                return;
            }

            if (!inFlowInstanceRoute) {
                const localPrimitive = primitive ?? store.primitive(routeFlowInstanceId);
                if (!localPrimitive) {
                    logLoadDebug("load_effect:fetchPrimitive", { id: routeFlowInstanceId });
                    store.fetchPrimitive(routeFlowInstanceId);
                }
                return;
            }

            if (loadingFlowInstanceIdRef.current === routeFlowInstanceId) {
                logLoadDebug("load_effect:skip_already_loading_same_id", { routeFlowInstanceId });
                return;
            }

            if (store.loadedWorkspaceMode === "flowinstance" && store.loadedWorkspaceFocusId === routeFlowInstanceId) {
                logLoadDebug("load_effect:skip_already_loaded_exact_focus", { routeFlowInstanceId });
                return;
            }

            loadingFlowInstanceIdRef.current = routeFlowInstanceId;
            setIsWorkspaceLoading(true);
            try {
                const localPrimitive = primitive ?? store.primitive(routeFlowInstanceId);
                if (localPrimitive?.type === "flowinstance" && localPrimitive.workspaceId) {
                    logLoadDebug("load_effect:loadActiveWorkspace:start", {
                        workspaceId: localPrimitive.workspaceId,
                        focusId: routeFlowInstanceId
                    });
                    await store.loadActiveWorkspace(localPrimitive.workspaceId, {
                        mode: "flowinstance",
                        focusId: routeFlowInstanceId,
                        background: true
                    });
                    logLoadDebug("load_effect:loadActiveWorkspace:done", {
                        workspaceId: localPrimitive.workspaceId,
                        focusId: routeFlowInstanceId
                    });
                } else {
                    logLoadDebug("load_effect:loadFlowInstanceById:start", { routeFlowInstanceId });
                    await store.loadFlowInstanceById(routeFlowInstanceId, { background: true });
                    logLoadDebug("load_effect:loadFlowInstanceById:done", { routeFlowInstanceId });
                }
            } finally {
                finishLoad();
            }
        };

        loadFlowData();
        return () => {
            cancelled = true;
            logLoadDebug("load_effect:cleanup", { id: routeFlowInstanceId, path: location.pathname });
        };
    }, [id, location.pathname]);

    useLayoutEffect(() => {
        setAppHeaderVisible(false);
        return () => setAppHeaderVisible(true);
    }, [setAppHeaderVisible]);

    const isForNewInstance = resolvedPrimitive?.type === "flow";
    const targetFlow = isForNewInstance ? resolvedPrimitive : resolvedPrimitive?.origin;

    const workspace = targetFlow?.workspace;
    const {
        workspaceGradient,
        accentButtonClassName,
        accentColor,
        accentTextClassName,
        accentIndicatorClassName,
        workspaceLogo,
    } = useWorkspaceBranding(workspace, {logoSize: "raw"});

    const flowStatus = resolvedPrimitive?.processing?.flow?.status ?? "not_started";
    const viewState =
        !resolvedPrimitive || !targetFlow
            ? "loading"
            : (isForNewInstance || flowStatus === "not_started")
            ? "new"
            : flowStatus === "complete"
            ? "complete"
            : "status";

    useEffect(() => {
        if (viewState === "complete") {
            setCompleteView("output");
        }
    }, [viewState]);

    useEffect(() => {
        if (inspectorFocus?.imageGallery?.type !== "image_gallery") {
            return;
        }
        const galleryImages = (Array.isArray(inspectorFocus.imageGallery.images) ? inspectorFocus.imageGallery.images : [])
            .filter((entry) => typeof entry?.url === "string" && entry.url.trim().length > 0);
        const skippedSourceIds = new Set(
            [inspectorFocus.imageGallery.skipSourceIds]
                .flat()
                .map((value) => (value === undefined || value === null ? undefined : `${value}`.trim()))
                .filter(Boolean)
        );
        const nextSelection = {};
        galleryImages.forEach((image, idx) => {
            const key = galleryImageKey(image, idx);
            const sourceId = image?.primitiveId ? `${image.primitiveId}` : undefined;
            nextSelection[key] = sourceId ? !skippedSourceIds.has(sourceId) : true;
        });
        setGallerySelectionByKey(nextSelection);
    }, [inspectorFocus?.imageGallery]);

    useEffect(() => {
        if (!resolvedPrimitive || viewState === "new" || viewState === "loading") {
            return;
        }
        const needsStatusData =
            viewState === "status" ||
            (viewState === "complete" &&
                (completeView === "overview" ||
                    completeView === "progress" ||
                    completeView === "timeline" ||
                    completeView === "output"));
        if (!needsStatusData) {
            return;
        }
        const statusPromise = resolvedPrimitive.instanceStatus;
        if (!statusPromise?.then) {
            return;
        }
        statusPromise.then((d) => {
            const { nodes, edges } = PrimitiveConfig.flowInstanceStatusToMap(d, {
                showHidden: false,
                showSkipped: false,
                groupByLabels: completeView === "progress",
            });
            const depth = PrimitiveConfig.convertEdgesToDepth(edges);

            nodes.forEach((node) => {
                node.depth = depth.get(node.id);
            });

            const steps = nodes
                .sort((a, b) => a.depth - b.depth)
                .map((node) => ({
                    title: node.name,
                    status: node.status,
                    ids: node.itemIds,
                    details: node,
                }));

            const timeline = nodes
                .flatMap((node) => {
                    const multiple = node.itemIds.length > 1;
                    let minStart;
                    let maxEnd;
                    const lineItems = node.itemIds.map((itemId) => {
                        const p = MainStore().primitive(itemId);
                        if (p.type === "page") {
                            return undefined;
                        }
                        let start =
                            p.type === "flowinstance"
                                ? p.processing?.flow?.started
                                : p.processing?.run_step?.pending;
                        let end =
                            p.type === "flowinstance"
                                ? p.processing?.flow?.completed
                                : p.processing?.run_step?.completed;

                        if (p.type === "search") {
                            start = p.processing?.query?.running;
                            end = p.processing?.query?.completed ?? new Date();
                            const children = p.primitives.origin.allSearch;
                            if (children) {
                                for (const child of children) {
                                    const childStart = child.processing?.query?.running;
                                    const childEnd = child.processing?.query?.completed;
                                    if (!start || (childStart && childStart < start)) {
                                        start = childStart;
                                    }
                                    if (!end || (childEnd && childEnd > end)) {
                                        end = childEnd;
                                    }
                                }
                            }
                        }

                        if (!(start && end)) {
                            return undefined;
                        }
                        if (!minStart || start < minStart) {
                            minStart = start;
                        }
                        if (!maxEnd || end > maxEnd) {
                            maxEnd = end;
                        }
                        return {
                            id: p.id,
                            title: multiple ? p.configParent.title : node.name,
                            start,
                            end,
                            parentId: multiple ? node.id : undefined,
                            childrenIds: multiple
                                ? undefined
                                : edges.filter((edge) => edge.from === itemId).map((edge) => edge.to),
                        };
                    });
                    if (multiple) {
                        lineItems.unshift({
                            id: node.id,
                            title: node.name,
                            start: minStart,
                            end: maxEnd,
                            childrenIds: edges
                                .filter((edge) => edge.from === node.id)
                                .map((edge) => edge.to),
                        });
                    }
                    return lineItems;
                })
                .filter(Boolean);

            setTimelineData(timeline);
            setStepsToProcess(steps);
            setStatusMap(d);
        });
    }, [resolvedPrimitive?.id, viewState, completeView]);

    const ids = resolvedPrimitive ? [resolvedPrimitive.id, ...resolvedPrimitive.primitives.origin.allIds] : [];
    useDataEvent("relationship_update set_field set_parameter", ids, () => {
        forceUpdate();
    });

    const buildSourceFocus = (primitives = [], options = {}) => {
        if (!primitives.length) {
            return undefined;
        }
        const primary = primitives[0];
        const store = MainStore();
        const unique = (items = []) => {
            const resolved = items.filter(Boolean).map(item =>
                typeof item === "object" && item.id ? item : store.primitive(item)
            ).filter(Boolean);
            return store.uniquePrimitives ? store.uniquePrimitives(resolved) : resolved;
        };
        const directSources = unique([
            ...(primary?.primitives?.relationship?.allItems ?? []),
            ...(primary?.primitives?.source?.allItems ?? []),
            ...(primary?.primitives?.link?.allItems ?? []),
        ]);

        const explicitSourceList = unique(options?.sourceList ?? []);
        const explicitPoolList = unique(options?.poolList ?? []);
        const explicitDefaultList = unique(options?.list ?? []);

        let sourceItems = explicitSourceList;
        if (!sourceItems.length && (primary?.type === "summary" || primary?.type === "query")) {
            sourceItems = unique(primary?.getResultSources ?? []);
        }
        if (!sourceItems.length && directSources.length) {
            sourceItems = directSources;
        }
        if (!sourceItems.length && explicitDefaultList.length) {
            sourceItems = explicitDefaultList;
        }
        let generator = primary;
        if (!["summary", "query"].includes(primary?.type)) {
            generator =
                primary?.findParentPrimitives?.({ type: ["summary", "query"], first: true })?.[0] ??
                primary?.configParent ??
                primary;
        }
        const availableSources =
            explicitPoolList.length > 0
                ? explicitPoolList
                : unique([
                    ...(generator?.itemsForProcessingWithOptions?.(undefined, { forceImports: true }) ?? []),
                    ...(generator?.itemsForProcessing ?? []),
                    ...(generator?.primitives?.relationship?.allItems ?? []),
                    ...(generator?.primitives?.source?.allItems ?? []),
                    ...(generator?.primitives?.link?.allItems ?? []),
                ]);
        return { sourceItems, availableSources, generator };
    };

    function newInstanceCallback(key, value) {
        setDataForNewInstance((prev) => ({
            ...prev,
            [key]: value,
        }));
        return true;
    }

    const pins = useMemo(() => {
        if (!resolvedPrimitive?.getConfig?.inputPins) {
            return {};
        }
        if (!isEmbedded) {
            return resolvedPrimitive.getConfig.inputPins;
        }
        return {
            ...resolvedPrimitive.getConfig.inputPins,
            split1: { split: true, title: "Your details" },
            ext_name: {
                name: "Your Name",
                source: "param.ext_name",
                types: ["string"],
            },
            ext_company_name: {
                name: "Your company",
                source: "param.ext_company_name",
                types: ["string"],
            },
            ext_email: {
                name: "Your company email (for report)",
                source: "param.ext_email",
                types: ["string"],
            },
        };
    }, [resolvedPrimitive?.id, isEmbedded]);

    const configurationOptions = useMemo(
        () => targetFlow?.referenceParameters?.configurations ?? {},
        [targetFlow?.id, targetFlow?.referenceParameters?.configurations]
    );
    const hasConfiguration = Object.keys(configurationOptions).length > 0;

    useEffect(() => {
        if (!isForNewInstance || !hasConfiguration) {
            return
        }
        setDataForNewInstance((prev) => {
            const next = { ...prev }
            let changed = false
            Object.entries(configurationOptions).forEach(([rawKey, info]) => {
                const key = `fc_${rawKey}`
                const currentValue = prev[key]
                const normalizedCurrent = normalizeConfigurationSelection(info, currentValue)
                if (info?.can_select_multiple === true) {
                    const hasCurrent = Array.isArray(currentValue) && currentValue.length > 0
                    if (!hasCurrent && Array.isArray(normalizedCurrent) && normalizedCurrent.length > 0) {
                        next[key] = normalizedCurrent
                        changed = true
                    }
                    return
                }
                const hasCurrent = typeof currentValue === "string" && currentValue.trim().length > 0
                if (!hasCurrent && typeof normalizedCurrent === "string" && normalizedCurrent.length > 0) {
                    next[key] = normalizedCurrent
                    changed = true
                }
            })
            return changed ? next : prev
        })
    }, [isForNewInstance, hasConfiguration, configurationOptions, targetFlow?.id])

    const isUpstreamImportEnabled = Boolean(!isForNewInstance && targetFlow?.referenceParameters?.importFromFIs === true)

    const pinsToShow = useMemo(() => {
        const relayProvidedPins = new Set()
        if (!isForNewInstance && isUpstreamImportEnabled && resolvedPrimitive) {
            const inputMap = PrimitiveConfig.getInputMap(resolvedPrimitive) ?? []
            inputMap.forEach((entry) => {
                if (typeof entry?.sourcePin === "string" && entry.sourcePin.startsWith("relay-") && entry?.inputPin) {
                    relayProvidedPins.add(entry.inputPin)
                }
            })
        }
        return Object.entries(pins ?? {}).reduce((acc, [key, info]) => {
            if (isUpstreamImportEnabled && (key.startsWith("relay-") || relayProvidedPins.has(key))) {
                return acc
            }
            let include = true;
            if (info.validForConfigurations) {
                include = info.validForConfigurations.some((config) => {
                    const configKey = `fc_${config.config}`;
                    const sourceValue = isForNewInstance
                        ? dataForNewInstance[configKey]
                        : resolvedPrimitive?.referenceParameters?.[configKey]
                    const configDefinition = configurationOptions?.[config.config]
                    const currentValue = [normalizeConfigurationSelection(configDefinition, sourceValue)].flat();
                    return [config.values].flat().some((value) => currentValue.includes(value));
                });
            }
            if (include) {
                acc[key] = info;
            }
            return acc;
        }, {});
    }, [pins, dataForNewInstance, resolvedPrimitive, resolvedPrimitive?.referenceParameters, isForNewInstance, configurationOptions, isUpstreamImportEnabled]);

    const inputs = resolvedPrimitive?.itemsForProcessing ?? [];
    const enableSubmit = Object.values(missing).filter(Boolean).length === 0;
    const [validFlowForUpstream, setValidFlowForUpstream] = useState(null)
    const [upstreamCandidates, setUpstreamCandidates] = useState([])
    async function ensurePrimitiveLoadedPrivate(primitiveId, workspaceId) {
        if (!primitiveId) {
            return null
        }
        let loaded = mainstore.primitive(primitiveId)
        if (loaded) {
            return loaded
        }
        if (!workspaceId) {
            return null
        }
        const params = new URLSearchParams({
            workspace: `${workspaceId}`,
            mode: "flowinstance",
            focus: `${primitiveId}`,
        })
        const response = await fetch(`/api/primitives?${params.toString()}`)
        if (!response.ok) {
            return null
        }
        await mainstore.consumePrimitiveStream(response, { reset: false, trackProgress: false })
        loaded = mainstore.primitive(primitiveId)
        return loaded ?? null
    }
    useEffect(() => {
        let cancelled = false
        async function loadUpstreamCandidates() {
            if (!isUpstreamImportEnabled || !resolvedPrimitive || !targetFlow) {
                if (!cancelled) {
                    setValidFlowForUpstream(null)
                    setUpstreamCandidates([])
                }
                return
            }

            let parentFlow = targetFlow
            if (!parentFlow?.primitives?.validflow?.allIds?.length && parentFlow?.id) {
                const refreshedParent = await ensurePrimitiveLoadedPrivate(parentFlow.id, parentFlow.workspaceId ?? resolvedPrimitive?.workspaceId)
                parentFlow = refreshedParent ?? parentFlow
            }

            const validFlowId = parentFlow?.primitives?.validflow?.allIds?.[0]
            if (!validFlowId) {
                if (!cancelled) {
                    setValidFlowForUpstream(null)
                    setUpstreamCandidates([])
                }
                return
            }

            const validFlow = await ensurePrimitiveLoadedPrivate(validFlowId, parentFlow.workspaceId ?? resolvedPrimitive?.workspaceId)
            if (!validFlow) {
                if (!cancelled) {
                    setValidFlowForUpstream(null)
                    setUpstreamCandidates([])
                }
                return
            }

            const instanceIds = (validFlow?.primitives?.config?.allIds ?? []).map((id) => `${id}`)
            for (const id of instanceIds) {
                await ensurePrimitiveLoadedPrivate(id, validFlow.workspaceId ?? resolvedPrimitive?.workspaceId)
            }

            const candidates = instanceIds
                .map((id) => mainstore.primitive(id))
                .filter((entry) => entry?.type === "flowinstance")
                .sort((a, b) => `${a?.title ?? ""}`.localeCompare(`${b?.title ?? ""}`))

            if (!cancelled) {
                setValidFlowForUpstream(validFlow)
                setUpstreamCandidates(candidates)
            }
        }
        loadUpstreamCandidates()
        return () => {
            cancelled = true
        }
    }, [isUpstreamImportEnabled, resolvedPrimitive?.id, resolvedPrimitive?.workspaceId, targetFlow?.id, update])
    const selectedUpstreamIds = useMemo(() => {
        if (!resolvedPrimitive) {
            return []
        }
        const ids = resolvedPrimitive?.primitives?.upstream?.allIds ?? []
        return ids.map((id) => `${id}`)
    }, [resolvedPrimitive?.id, update])

    function setValue(key, value) {
        if (isForNewInstance) {
            newInstanceCallback(key, value);
        } else {
            resolvedPrimitive?.setField(`referenceParameters.${key}`, value);
        }
    }



    async function syncUpstreamRelationships(nextIds = []) {
        if (!resolvedPrimitive) {
            return
        }
        const currentIds = new Set((resolvedPrimitive?.primitives?.upstream?.allIds ?? []).map((id) => `${id}`))
        const nextIdSet = new Set(nextIds.map((id) => `${id}`))
        const candidateMap = new Map(upstreamCandidates.map((candidate) => [`${candidate.id}`, candidate]))

        for (const currentId of currentIds) {
            if (nextIdSet.has(currentId)) {
                continue
            }
            let target = candidateMap.get(currentId) ?? mainstore.primitive(currentId)
            if (!target) {
                target = await ensurePrimitiveLoadedPrivate(currentId, resolvedPrimitive?.workspaceId)
            }
            if (target) {
                await resolvedPrimitive.removeRelationship(target, "upstream")
            }
        }
        for (const nextId of nextIdSet) {
            if (currentIds.has(nextId)) {
                continue
            }
            let target = candidateMap.get(nextId) ?? mainstore.primitive(nextId)
            if (!target) {
                target = await ensurePrimitiveLoadedPrivate(nextId, resolvedPrimitive?.workspaceId)
            }
            if (target) {
                await resolvedPrimitive.addRelationship(target, "upstream")
            }
        }
    }

    function createNewInstance() {
        if (isForNewInstance) {
            MainStore().doPrimitiveAction(targetFlow, "create_flowinstance", { data: dataForNewInstance });
        }
    }

    function renderConfigOption(rawKey, info) {
        const key = `fc_${rawKey}`;
        const sourceValue = isForNewInstance ? dataForNewInstance[key] : resolvedPrimitive?.referenceParameters?.[key]
        const normalizedValue = normalizeConfigurationSelection(info, sourceValue)
        const currentValue = [normalizedValue].flat().filter((value) => `${value ?? ""}`.trim().length > 0);
        switch (info.type) {
            case "options":
                return (
                    <Select
                        className="max-w-xs"
                        label="Select option"
                        variant="bordered"
                        selectedKeys={currentValue ?? []}
                        selectionMode={info.can_select_multiple === true ? "multiple" : "single"}
                        onChange={(e) => {
                            const value = e.target.value;
                            setValue(
                                key,
                                info.can_select_multiple === true
                                    ? value.split(",").map((item) => item.trim()).filter(Boolean)
                                    : value
                            );
                        }}
                    >
                        {info.options.map((option) => (
                            <SelectItem key={option.id}>{option.title}</SelectItem>
                        ))}
                    </Select>
                );
            default:
                return null;
        }
    }

    const workspaceHeader = (navConfig) => {
        const statusLabel =
            {
                not_started: undefined,
                running: "In progress",
                paused: "Paused",
                complete: "Completed",
                failed: "Needs attention",
            }[flowStatus] ;
        return (<>
                <div className="relative overflow-hidden bg-slate-900 text-white shadow-xl ring-1 ring-black/10 z-20">
                    <div
                        className="absolute inset-0"
                        style={{ backgroundImage: workspaceGradient, backgroundSize: "cover" }}
                    />
                    <div className="absolute inset-0 bg-slate-900/35" />
                    <div className="relative flex flex-col gap-6 px-6 py-6">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-center gap-3">
                                <AppMenuButton
                                    size="lg"
                                    radius="sm"
                                    variant="flat"
                                    className="border border-white/20 bg-white/10 text-white hover:bg-white/20"
                                />
                                <div className="flex flex-col">

                                    <span className="text-xs uppercase tracking-wide text-white/70">
                                        {targetFlow?.title ?? resolvedPrimitive?.title ?? "Flow"} • #{resolvedPrimitive?.plainId ?? "—"}
                                    </span>
                                    <h1 className="text-2xl font-semibold text-white">
                                        {resolvedPrimitive?.title ?? targetFlow?.title ?? "Flow"}
                                    </h1>
                                </div>
                                <div className="flex gap-2 text-right place-items-center place-self-end">
                                    {statusLabel && <span className="inline-flex items-center rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white/80">
                                        {statusLabel}
                                    </span>}
                                    <div className="flex items-center justify-end gap-2">
                                        {!isForNewInstance && viewState !== "new" && (flowStatus === "not_started" || flowStatus === "failed") && (
                                            <Button
                                                className={accentButtonClassName}
                                                onClick={() =>
                                                    MainStore().doPrimitiveAction(resolvedPrimitive, "continue_flow_instance", {
                                                        organizationId: MainStore().activeOrganization?.id,
                                                    })
                                                }
                                                startContent={<Icon icon="solar:play-circle-linear" className="h-5 w-5" />}
                                            >
                                                {flowStatus === "not_started" ? "Run" : "Continue"}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col items-center gap-3">
                                <div className="flex space-x-3 justify-end">
                                    <div className="bg-white/20 border-white/20 border rounded-3xl flex space-x-2 p-1">
                                        {workspaceLogo}
                                        <UserMenuButton
                                            renderTrigger={({ userInfo }) => (
                                                <Button
                                                    isIconOnly
                                                    variant="light"
                                                    radius="full"
                                                    className="border border-white/20 bg-white/10 text-white hover:bg-white/20"
                                                >
                                                    <Avatar
                                                        radius="full"
                                                        size="md"
                                                        src={userInfo?.avatarUrl}
                                                        className="border-2 border-white/30"
                                                    />
                                                </Button>
                                            )}
                                            menuProps={{ variant: "flat" }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                        <div className="relative pl-2 pr-6 pb-4 flex flex-col lg:flex-row min-h-12">
                            <Button
                                variant="light"
                                color={accentColor ? "default" : "primary"}
                                radius="sm"
                                className="text-white/70 group-data-[selected=true]:text-white"
                                onPress={()=>navigate(`/workflows/${activeWorkspaceId}`)}
                                >
                                {"< Back"}
                            </Button>
                                {navConfig?.items?.length ? (
                                    <div className="mx-auto flex items-center gap-3">
                                        <Tabs
                                            aria-label="Flow views"
                                            variant="underlined"
                                            selectedKey={navConfig.selectedKey}
                                            onSelectionChange={navConfig.onSelectionChange}
                                            className="px-2 text-white"
                                            classNames={{
                                                tabContent: "text-white/70 group-data-[selected=true]:text-white",
                                                tabList: "gap-4",
                                                cursor: accentIndicatorClassName ?? "bg-white",
                                            }}
                                        >
                                            {navConfig.items.map((item) => (
                                                <Tab key={item.id} title={item.title} isDisabled={item.disabled} />
                                            ))}
                                        </Tabs>
                                        {viewState === "complete" && (
                                            <Button
                                                variant="light"
                                                radius="full"
                                                size="sm"
                                                className="border border-white/25 bg-white/15 text-white hover:bg-white/25"
                                                startContent={<DocumentArrowDownIcon className="h-4 w-4" />}
                                                onPress={() =>
                                                    thumbnailsRef?.current?.downloadAll?.({
                                                        title: resolvedPrimitive?.title ?? "flow",
                                                        includeCoverSlide: true,
                                                    })
                                                }
                                            >
                                                Download
                                            </Button>
                                        )}
                                    </div>
                                ) : null}
                                    {!isExternalUser && (
                                        <div className="fixed flex place-items-center pt-2 right-6 space-x-2">
                                            <span className="text-xs uppercase tracking-wide text-white/70">
                                                {workspace?.title ?? "Workspace"}
                                            </span>
                                            <div className="flex justify-end">
                                                <CreditsBadge tone="dark" className="w-[98px] text-center" radius="full"/>
                                            </div>
                                        </div>
                                    )}
                        </div>
                </div>
                </>
        );
    };

    const completeNavItems = useMemo(() => {
        if (viewState !== "complete") {
            return [];
        }
        const items = [
            { id: "output", title: "Output" },
            { id: "overview", title: "Overview" },
            { id: "inputs", title: "Inputs" },
        ];
        if (!isExternalUser) {
            items.push(
                { id: "progress", title: "Progress" },
                { id: "timeline", title: "Timeline" }
            );
        }
        return items;
    }, [viewState, inspectorFocus, resolvedPrimitive?.processing?.run_flow_instance, isExternalUser]);

    useEffect(() => {
        if (viewState === "complete" && completeNavItems.length > 0) {
            const activeExists = completeNavItems.some((item) => item.id === completeView && !item.disabled);
            if (!activeExists) {
                setCompleteView(completeNavItems[0].id);
            }
        }
    }, [viewState, completeNavItems, completeView]);

    const renderInspectorPanel = (widthClass = "w-[24rem] max-w-[24rem]") => {
        if (!inspectorFocus?.primitives?.length) {
            return null;
        }
        // Convert legacy sourceFocus to vizProvenance for unified rendering
        if (inspectorFocus?.sourceFocus && !inspectorFocus?.vizProvenance) {
            const { sourceItems, generator } = inspectorFocus.sourceFocus;
            const primary = inspectorFocus.primitives?.[0];
            const summaryPrim = inspectorFocus.summaryPrimitive
                ?? sourceItems.find(s => s.type === "summary" && s.referenceParameters?.structured_summary)
                ?? primary;
            const structuredSummary = summaryPrim?.referenceParameters?.structured_summary;
            let provenances = [];
            if (Array.isArray(structuredSummary) && structuredSummary.length > 0) {
                provenances = structuredSummary.map(section => ({
                    key: section.heading || "",
                    evidence: section.content || "",
                    verified: true,
                    sourceIds: section.ids || [],
                    _idMap: section.id_map || {},
                    _quotes: section.quote || [],
                }));
            }
            inspectorFocus = {
                ...inspectorFocus,
                vizProvenance: {
                    blockId: primary?.title ?? primary?.plainId ?? "Sources",
                    provenances,
                    sourceList: sourceItems.length > 0 ? sourceItems : undefined,
                    sourceIds: sourceItems.length === 0 ? (generator?.getResultSources?.map(s => s.id) ?? []) : undefined,
                    summaryId: summaryPrim?.id,
                },
            };
        }
        if (inspectorFocus?.sourceFocus && !inspectorFocus?.vizProvenance) {
            return renderInspectorPanel(widthClass);
        }
        const primary = inspectorFocus.primitives[0];
        if (!primary) {
            return null;
        }
        const statusEntry =
            statusMap?.[primary.id] ??
            Object.values(statusMap ?? {}).find(
                (entry) => entry?.primitive?.id === primary.id || entry?.id === primary.id
            );

        if (primary.type === "flowcontrol") {
            const evaluation = statusEntry?.flowControl ?? {};
            const states = primary.referenceParameters?.flowControl?.states ?? [];
            const activePins = evaluation.activePins ?? {};
            const pendingSources = (evaluation.pendingSources ?? [])
                .map((pid) => (typeof pid === "object" ? pid : MainStore().primitive(pid)))
                .filter(Boolean);

            const comparatorLabels = {
                equals: "equals",
                not_equals: "≠",
                contains: "contains",
                not_contains: "does not contain",
                starts_with: "starts with",
                ends_with: "ends with",
                gt: ">",
                gte: "≥",
                lt: "<",
                lte: "≤",
                in: "in",
                not_in: "not in",
            };

            const describeCondition = (condition = {}) => {
                const comparator = comparatorLabels[condition.comparator] ?? condition.comparator ?? "equals";
                const value = Array.isArray(condition.value)
                    ? condition.value.join(", ")
                    : condition.value ?? (condition.metric === "count" ? 0 : "");
                switch (condition.metric) {
                    case "title":
                        return `Title ${comparator} "${value}"`;
                    case "parameter":
                        return `Parameter ${condition.parameterPath ?? "(select)"} ${comparator} "${value}"`;
                    case "count":
                        return `Count ${comparator} ${value ?? 0}`;
                    case "error":
                        return `Status ${comparator} ${value ?? "error"}`;
                    default:
                        return `${condition.metric ?? "value"} ${comparator} ${value}`;
                }
            };

            const evaluationStatus = evaluation.haltFlow
                ? "Flow halted"
                : evaluation.evaluated
                ? "Evaluated"
                : "Awaiting inputs";

            const evaluationColor = evaluation.haltFlow
                ? "text-red-600"
                : evaluation.evaluated
                ? "text-emerald-600"
                : "text-slate-500";

            return (
                <div className={clsx("flex h-full min-h-0 flex-col rounded-2xl bg-white p-4 shadow-lg", widthClass)}>
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <p className="text-sm font-semibold text-slate-700">
                                {primary.title ?? primary.metadata?.title ?? "Flow control"}
                            </p>
                            <p className="text-xs text-slate-500">{primary.plainId}</p>
                        </div>
                        <Button size="sm" variant="light" onClick={() => setInspectorFocus(undefined)}>
                            Close
                        </Button>
                    </div>
                    <div className={clsx("mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs", evaluationColor)}>
                        {evaluationStatus}
                        {evaluation.haltFlow && <span className="block text-slate-600 mt-1">Downstream execution is paused.</span>}
                    </div>
                    {Object.keys(activePins).length > 0 && (
                        <div className="mt-4">
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Pins</p>
                            <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                {Object.entries(activePins).map(([pin, enabled]) => (
                                    <span
                                        key={pin}
                                        className={clsx(
                                            "rounded-full border px-2 py-1",
                                            enabled
                                                ? "border-emerald-500 bg-emerald-50 text-emerald-600"
                                                : "border-slate-200 bg-white text-slate-500"
                                        )}
                                    >
                                        {pin}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {pendingSources.length > 0 && (
                        <div className="mt-4">
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Pending inputs</p>
                            <ul className="mt-2 list-disc pl-4 text-xs text-slate-600">
                                {pendingSources.map((p) => (
                                    <li key={p.id}>{p.title ?? p.plainId ?? p.id}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <div className="mt-4 flex-1 min-h-0 overflow-y-auto">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">States</p>
                        <div className="mt-2 space-y-3">
                            {states.length === 0 && (
                                <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-500">
                                    No states configured.
                                </div>
                            )}
                            {states.map((state, idx) => {
                                const stateKey = state.id ?? state.pinKey ?? `state-${idx}`;
                                const runtimeState =
                                    evaluation.stateResults?.[state.id] ??
                                    evaluation.stateResults?.[state.pinKey] ??
                                    evaluation.stateResults?.[stateKey];
                                const pending = runtimeState?.pending;
                                const passed = runtimeState?.passed === true;
                                const stateStatus = evaluation.haltFlow && state.haltFlow && passed
                                    ? "Halting"
                                    : passed
                                    ? "Active"
                                    : pending || !evaluation.evaluated
                                    ? "Pending"
                                    : "Inactive";
                                const stateStatusColor =
                                    evaluation.haltFlow && state.haltFlow && passed
                                        ? "text-red-600"
                                        : passed
                                        ? "text-emerald-600"
                                        : pending || !evaluation.evaluated
                                        ? "text-slate-500"
                                        : "text-slate-400";

                                return (
                                    <div key={stateKey} className="rounded-lg border border-slate-200 bg-white p-3">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <p className="text-sm font-medium text-slate-700">
                                                    {state.name ?? state.pinKey ?? `State ${idx + 1}`}
                                                </p>
                                                {state.pinKey && (
                                                    <p className="text-xs text-slate-500">Pin: {state.pinKey}</p>
                                                )}
                                            </div>
                                            <span className={clsx("text-xs font-medium", stateStatusColor)}>{stateStatus}</span>
                                        </div>
                                        {state.conditions?.length > 0 && (
                                            <div className="mt-3 space-y-2">
                                                {state.conditions.map((condition, cIdx) => {
                                                    const condId =
                                                        condition.id ??
                                                        `${stateKey}-cond-${cIdx}`;
                                                    const condResult = runtimeState?.conditions?.[condId];
                                                    const indicator = condResult === true ? "✔" : condResult === false ? "✖" : "…";
                                                    const indicatorColor =
                                                        condResult === true
                                                            ? "text-emerald-500"
                                                            : condResult === false
                                                            ? "text-red-500"
                                                            : "text-slate-400";
                                                    return (
                                                        <div key={condId} className="flex items-start gap-2 text-xs text-slate-600">
                                                            <span className={clsx("mt-0.5 text-xs", indicatorColor)}>{indicator}</span>
                                                            <span>{describeCondition(condition)}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {state.haltFlow && (
                                            <p className="mt-3 text-xs font-medium text-red-500">
                                                This state halts the flow when active.
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            );
        }

        const SourceChainPanel = ({ evidenceId, chain }) => {
            const mainstore = MainStore();
            if (!chain || chain.length === 0) return null;
            return (
                <div className="px-3 py-2">
                        {chain.map(({ prim, quotes, depth }, i) => {
                            if (!prim) return null;
                            const pad = depth * 16;
                            const sourceUrl = prim.referenceParameters?.url ?? prim.url ?? prim.metadata?.url;
                            const isTop = depth === 0;
                            return (
                                <div key={prim.id ?? i} style={{ paddingLeft: `${pad}px` }} className="mb-2">
                                    {quotes.length > 0 && (
                                        <p className={`text-sm text-slate-600 italic leading-snug break-words ${isTop ? '' : 'border-l-2 border-slate-300 pl-2'}`}>
                                            "{quotes[0]}"
                                        </p>
                                    )}
                                    <div className={`flex items-center gap-2 min-w-0 pb-1 ${isTop ? '' : 'border-l-2 border-slate-300 pl-2'}`}>
                                        {sourceUrl && (
                                            <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="shrink-0 text-slate-400 hover:text-blue-600" onClick={e => e.stopPropagation()} title="Open source">
                                                <LinkIcon className="h-3.5 w-3.5" />
                                            </a>
                                        )}
                                        <div className="flex-1 min-w-0 w-0">
                                            <p className="text-xs text-slate-400 truncate">{prim.title ?? prim.name ?? `#${prim.plainId ?? prim.id}`}</p>
                                            {prim.plainId && <p className="text-[10px] text-slate-400 truncate">#{prim.plainId}</p>}
                                        </div>
                                        <button
                                            type="button"
                                            className="flex items-center justify-center h-6 w-6 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 shrink-0"
                                            title="Open in sidebar"
                                            onClick={() => mainstore.sidebarSelect?.(prim)}>
                                            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            };

        if (inspectorFocus?.vizProvenance) {
            const { blockId, provenances } = inspectorFocus.vizProvenance;

            return (
                <div className={clsx("flex h-full min-h-0 flex-col rounded-2xl bg-white shadow-lg", widthClass)}>
                    <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
                        <div>
                            <p className="text-sm font-semibold text-slate-700">{blockId ?? "Block provenance"}</p>
                            <p className="text-xs text-slate-500">{provenances?.length ?? 0} evidence quote{(provenances?.length ?? 0) !== 1 ? "s" : ""}</p>
                        </div>
                        <Button size="sm" variant="light" onClick={() => setInspectorFocus(undefined)}>Close</Button>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
                        {/* When sourceFocus provides items but no provenances,
                            treat each source as a provenance entry */}
                        {(provenances?.length === 0 && (
                            inspectorFocus.vizProvenance.sourceIds?.length > 0 ||
                            inspectorFocus.vizProvenance.sourceList?.length > 0
                        )
                            ? (inspectorFocus.vizProvenance.sourceList || 
                               inspectorFocus.vizProvenance.sourceIds.map(sid => ({ id: sid }))
                              ).map(s => ({
                                key: s.title || s.name || (s.id || s).slice(0,12),
                                evidence: s.title || s.name || '',
                                verified: true,
                                sourceIds: [s.id || s],
                                _idMap: {},
                                _quotes: []
                            }))
                            : (provenances || [])
                        ).map((prov, i) => {
                            const dslQuote = prov.evidence;
                            const dslVerified = prov.verified !== false;

                            // If prov already has _idMap/_quotes (non-DSL structured_summary items),
                            // walk each sourceId with its quote from the prov.
                            // Otherwise (DSL block), scan the summary's structured_summary.
                            const hasProvQuotes = prov._idMap && Object.keys(prov._idMap).length > 0;
                            const summaryId = inspectorFocus?.vizProvenance?.summaryId ?? (prov.sourceIds?.[0] ?? null);
                            const sourceChains = hasProvQuotes
                                ? (prov.sourceIds || []).map(sid => {
                                    const idxs = prov._idMap[sid] ?? prov._idMap[`${sid}`] ?? [];
                                    const qForId = (Array.isArray(idxs) ? idxs : [])
                                        .map(i => ((prov._quotes ?? [])[i] ?? "").trim()).filter(Boolean);
                                    const result = buildSourceChain({
                                        evidenceId: sid,
                                        quoteForEvidence: qForId,
                                        summaryId
                                    });
                                    if (result.missingIds?.length) vizMissingRef.current.push(...result.missingIds);
                                    return { sid, chain: result.chain, missingIds: result.missingIds };
                                })
                                : (() => {
                                    const mstore = MainStore();
                                    const allSections = (() => {
                                        for (const sid of (prov.sourceIds || [])) {
                                            const prim = mstore.primitive(sid);
                                            if (!prim) continue;
                                            const ss = prim.referenceParameters?.structured_summary;
                                            if (Array.isArray(ss)) return ss;
                                        }
                                        return [];
                                    })();
                                    if (allSections.length === 0) {
                                        return (prov.sourceIds || []).map(sid => {
                                            const result = buildSourceChain({
                                                evidenceId: sid,
                                                quoteForEvidence: [],
                                                summaryId
                                            });
                                            if (result.missingIds?.length) vizMissingRef.current.push(...result.missingIds);
                                            return { sid, chain: result.chain, missingIds: result.missingIds };
                                        });
                                    }
                                    // Match evidence text to the correct section
                                    const norm = s => (s||'').toLowerCase().replace(/\s+/g,' ').trim();
                                    const evNorm = norm(dslQuote);
                                    let matchedSection = allSections.find(s => norm(s.content).includes(evNorm.slice(0,40)) || evNorm.includes(norm(s.content).slice(0,40)));
                                    if (!matchedSection && allSections.length === 1) matchedSection = allSections[0];
                                    const sectionsToWalk = matchedSection ? [matchedSection] : allSections;
                                    return sectionsToWalk.flatMap(section => {
                                        const secIds = Array.isArray(section.ids) ? section.ids : [];
                                        const secQuotes = Array.isArray(section.quote) ? section.quote : [];
                                        const secIdMap = section.id_map && typeof section.id_map === 'object' ? section.id_map : {};
                                        return secIds.map(sid => {
                                            const idxs = secIdMap[sid] ?? secIdMap[`${sid}`] ?? [];
                                            const qForId = (Array.isArray(idxs) ? idxs : [])
                                                .map(i => (secQuotes[i] ?? "").trim()).filter(Boolean);
                                            const result = buildSourceChain({
                                                evidenceId: sid,
                                                quoteForEvidence: qForId,
                                                summaryId
                                            });
                                            if (result.missingIds?.length) vizMissingRef.current.push(...result.missingIds);
                                            return { sid, chain: result.chain, missingIds: result.missingIds };
                                        });
                                    });
                                })();

                            // Group: if we have multiple sections from structured_summary,
                            // render each as a separate card with its heading.
                            const sectionCards = hasProvQuotes ? null : (() => {
                                const mstore2 = MainStore();
                                const allSections2 = (() => {
                                    for (const sid of (prov.sourceIds || [])) {
                                        const prim = mstore2.primitive(sid);
                                        if (!prim) continue;
                                        const ss = prim.referenceParameters?.structured_summary;
                                        if (Array.isArray(ss) && ss.length > 1) return ss;
                                    }
                                    return null;
                                })();
                                if (!allSections2) return null;
                                // Match evidence to the correct section
                                const norm2 = s => (s||'').toLowerCase().replace(/\s+/g,' ').trim();
                                const evNorm2 = norm2(dslQuote);
                                const matched2 = allSections2.find(s => norm2(s.content).includes(evNorm2.slice(0,40)) || evNorm2.includes(norm2(s.content).slice(0,40)));
                                const sectionsForCards = matched2 ? [matched2] : allSections2;
                                // Filter out bare Title sections
                                const filtered2 = sectionsForCards.filter(s => {
                                    if (s.heading === "Title" && !s.quote?.length && !Object.keys(s.id_map || {}).length) {
                                        return !sectionsForCards.some(o => o !== s && (o.quote?.length || Object.keys(o.id_map || {}).length));
                                    }
                                    return true;
                                });
                                if (filtered2.length <= 1) return null;
                                return filtered2.map(section => ({
                                    heading: section.heading || '',
                                    evidence: section.content || '',
                                    section
                                }));
                            })();

                            if (sectionCards) {
                                return sectionCards.map((sc, si) => {
                                    const sec = sc.section;
                                    const secIds = Array.isArray(sec.ids) ? sec.ids : [];
                                    const secQuotes = Array.isArray(sec.quote) ? sec.quote : [];
                                    const secIdMap = sec.id_map && typeof sec.id_map === 'object' ? sec.id_map : {};
                                    const chains = secIds.map(sid => {
                                        const idxs = secIdMap[sid] ?? secIdMap[`${sid}`] ?? [];
                                        const qForId = (Array.isArray(idxs) ? idxs : [])
                                            .map(i => (secQuotes[i] ?? "").trim()).filter(Boolean);
                                        const result = buildSourceChain({
                                            evidenceId: sid,
                                            quoteForEvidence: qForId,
                                            summaryId
                                        });
                                        if (result.missingIds?.length) vizMissingRef.current.push(...result.missingIds);
                                        return { sid, chain: result.chain };
                                    });
                                    return (
                                        <div key={`section-${si}`} className="rounded-lg border border-slate-200">
                                            <div className="px-3 py-2 bg-slate-50 rounded-t-lg border-b border-slate-200">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">✓</span>
                                                    <span className="text-xs text-slate-500 truncate">{sc.heading || `Section ${si + 1}`}</span>
                                                </div>
                                                {sc.evidence && <p className="text-sm text-slate-600 italic leading-relaxed">"{sc.evidence}"</p>}
                                            </div>
                                            <div className="divide-y divide-slate-100">
                                                {chains.map(({ sid, chain }) => (
                                                    <SourceChainPanel key={sid} evidenceId={sid} chain={chain} />
                                                ))}
                                            </div>
                                        </div>
                                    );
                                });
                            }

                            return (
                                <div key={i} className="rounded-lg border border-slate-200">
                                    <div className="px-3 py-2 bg-slate-50 rounded-t-lg border-b border-slate-200">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={clsx("text-xs font-medium px-2 py-0.5 rounded-full", dslVerified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700")}>
                                                {dslVerified ? "✓" : "⚠"}
                                            </span>
                                            <span className="text-xs text-slate-500 truncate">{prov.key?.split(".").pop() ?? blockId}</span>
                                        </div>
                                        <p className="text-sm text-slate-600 italic leading-relaxed">"{dslQuote}"</p>
                                    </div>
                                    {sourceChains.length > 0 ? (
                                        <div className="divide-y divide-slate-100">
                                            {sourceChains.map(({ sid, chain }) => (
                                                <SourceChainPanel key={sid} evidenceId={sid} chain={chain} />
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-slate-400 px-3 py-2">Sources not loaded: {(prov.sourceIds || []).join(", ") || "none"}</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }

        if (inspectorFocus?.sourceFocus) {
            const { sourceItems, availableSources, generator } = inspectorFocus.sourceFocus;
            const primary = inspectorFocus.primitives?.[0];
            const summaryPrim = inspectorFocus.summaryPrimitive
                ?? sourceItems.find(s => s.type === "summary" && s.referenceParameters?.structured_summary)
                ?? primary;
            const structuredSummary = summaryPrim?.referenceParameters?.structured_summary;

            // Build provenances from structured_summary if available
            let provenances = [];
            if (Array.isArray(structuredSummary) && structuredSummary.length > 0) {
                provenances = structuredSummary.map(section => ({
                    key: section.heading || "",
                    evidence: section.content || "",
                    verified: true,
                    sourceIds: section.ids || [],
                    _idMap: section.id_map || {},
                    _quotes: section.quote || [],
                }));
            }

            return (
                <div className={clsx("flex h-full min-h-0 flex-col rounded-2xl bg-white shadow-lg", widthClass)}>
                    <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
                        <div>
                            <p className="text-sm font-semibold text-slate-700">{primary?.title ?? primary?.plainId ?? "Sources"}</p>
                            <p className="text-xs text-slate-500">{sourceItems.length} source{sourceItems.length !== 1 ? "s" : ""}{provenances.length > 0 ? ` · ${provenances.length} evidence quote${provenances.length !== 1 ? "s" : ""}` : ""}</p>
                        </div>
                        <Button size="sm" variant="light" onClick={() => setInspectorFocus(undefined)}>Close</Button>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
                        {provenances.map((prov, i) => {
                            const sourceChains = (prov.sourceIds || []).map(sid => {
                                const idMap = prov._idMap ?? {};
                                const quotesArr = prov._quotes ?? [];
                                const idxs = idMap[sid] ?? idMap[`${sid}`] ?? [];
                                const qForId = (Array.isArray(idxs) ? idxs : [])
                                    .map(idx => quotesArr[idx] ?? "").filter(Boolean);
                                const result = buildSourceChain({
                                    evidenceId: sid,
                                    quoteForEvidence: qForId.length ? qForId : [prov.evidence],
                                    summaryId: summaryPrim?.id ?? null
                                });
                                if (result.missingIds?.length) vizMissingRef.current.push(...result.missingIds);
                                return { sid, chain: result.chain, missingIds: result.missingIds };
                            });
                            return (
                                <div key={i} className="rounded-lg border border-slate-200">
                                    <div className="px-3 py-2 bg-slate-50 rounded-t-lg border-b border-slate-200">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">✓</span>
                                            <span className="text-xs text-slate-500">{prov.key}</span>
                                        </div>
                                        <p className="text-sm text-slate-600 italic leading-relaxed">"{prov.evidence}"</p>
                                    </div>
                                    {sourceChains.length > 0 ? (
                                        <div className="divide-y divide-slate-100">
                                            {sourceChains.map(({ sid, chain }) => (
                                                <SourceChainPanel key={sid} evidenceId={sid} chain={chain} />
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-slate-400 px-3 py-2">No source chains resolved</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }
        if (inspectorFocus?.imageGallery?.type === "image_gallery") {
            const gallery = inspectorFocus.imageGallery;
            const allImages = (Array.isArray(gallery.images) ? gallery.images : []).filter(
                (entry) => typeof entry?.url === "string" && entry.url.trim().length > 0
            );
            const images = allImages;
            const showCaptions = gallery.showCaptions !== undefined ? Boolean(gallery.showCaptions) : true;
            const columnCountRaw = Number(gallery.columns);
            const columnCount = Number.isFinite(columnCountRaw) && columnCountRaw > 0
                ? Math.max(1, Math.min(4, Math.round(columnCountRaw)))
                : images.length <= 1
                ? 1
                : images.length === 2
                ? 2
                : 3;
            const totalImages = Number.isFinite(Number(gallery.totalImages))
                ? Number(gallery.totalImages)
                : allImages.length;

            return (
                <div className={clsx("flex h-full min-h-0 flex-col rounded-2xl bg-white shadow-lg", widthClass)}>
                    <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
                        <div>
                            <p className="text-sm font-semibold text-slate-700">{primary.title ?? primary.plainId}</p>
                            <p className="text-xs text-slate-500">{totalImages} image{totalImages === 1 ? "" : "s"}</p>
                        </div>
                        <Button size="sm" variant="light" onClick={() => setInspectorFocus(undefined)}>
                            Close
                        </Button>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto p-4">
                        {images.length === 0 ? (
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
                                {gallery.emptyText ?? "No images available"}
                            </div>
                        ) : (
                            <div
                                className="grid gap-2"
                                style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
                            >
                                {images.map((image, idx) => (
                                    <div key={galleryImageKey(image, idx)} className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                                        <div className="flex items-center justify-end border-b border-slate-200 bg-white px-2 py-1">
                                            <label className="flex items-center gap-1 text-[11px] font-medium text-slate-600">
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(gallerySelectionByKey[galleryImageKey(image, idx)])}
                                                    onChange={(event) => {
                                                        const checked = event.target.checked;
                                                        const flowInstanceId = gallery.flowInstanceId ? `${gallery.flowInstanceId}` : undefined;
                                                        const targetPrimitive = inspectorFocus?.primitives?.[0];
                                                        setGallerySelectionByKey((prev) => {
                                                            const nextSelection = {
                                                                ...prev,
                                                                [galleryImageKey(image, idx)]: checked,
                                                            };
                                                            if (targetPrimitive && flowInstanceId) {
                                                                const skipSourceIds = Array.from(
                                                                    new Set(
                                                                        images
                                                                            .filter((entry, imageIdx) => !nextSelection[galleryImageKey(entry, imageIdx)])
                                                                            .map((entry) => (entry?.primitiveId ? `${entry.primitiveId}` : undefined))
                                                                            .filter(Boolean)
                                                                    )
                                                                );
                                                                targetPrimitive.setField(
                                                                    `instanceConfig.${flowInstanceId}.imageGallery.skipSourceIds`,
                                                                    skipSourceIds
                                                                );
                                                            }
                                                            return nextSelection;
                                                        });
                                                    }}
                                                />
                                                Include
                                            </label>
                                        </div>
                                        <VFImage
                                            src={image.url}
                                            className="h-32 w-full object-cover"
                                            alt={image.label ?? `Gallery image ${idx + 1}`}
                                        />
                                        {image.primitiveId ? (
                                            <p className="truncate px-2 pt-1 text-[11px] text-slate-400">{`Source: ${image.primitiveId}`}</p>
                                        ) : null}
                                        {showCaptions && image.label ? (
                                            <p className="truncate px-2 py-1 text-xs text-slate-500">{image.label}</p>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return (
            <div className={clsx("flex h-full min-h-0 flex-col rounded-2xl bg-white shadow-lg", widthClass)}>
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                    <p className="text-sm font-semibold text-slate-700">{primary.title ?? primary.plainId}</p>
                    <Button size="sm" variant="light" onClick={() => setInspectorFocus(undefined)}>
                        Close
                    </Button>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto">
                    <FlowContent {...inspectorFocus} />
                </div>
            </div>
        );
    };

    const renderFlowOptionsCard = () => (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center gap-2.5 px-5 py-3.5 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
                <Icon icon="solar:settings-minimalistic-linear" className="h-4 w-4 text-slate-400" />
                <span className="text-sm font-semibold text-slate-700">Flow Options</span>
            </div>
            <div className="px-5 py-4 flex flex-wrap items-center gap-x-6 gap-y-3">
                <Checkbox
                    size="sm"
                    isSelected={
                        isForNewInstance
                            ? dataForNewInstance?.auto_run === true
                            : resolvedPrimitive?.referenceParameters?.auto_run === true
                    }
                    onValueChange={(selected) => setValue("auto_run", selected)}
                >
                    <span className="text-sm text-slate-700">Skip confirmation pauses</span>
                </Checkbox>
                {Object.entries(configurationOptions).map(([key, info]) => (
                    <div key={key} className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-600">{info.title}:</span>
                        {renderConfigOption(key, info)}
                    </div>
                ))}
            </div>
        </div>
    );

    const renderInputPanel = () => {
        return (
            <div className="flex h-full min-h-0 flex-col gap-5 overflow-y-auto w-full max-w-2xl mx-auto pt-6 pb-12">
                <PrimitiveCard.EditableTitle primitive={resolvedPrimitive}/>
                <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Workflow Inputs</span>
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
                </div>
                {renderFlowOptionsCard()}
                {isUpstreamImportEnabled && (
                    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                        <div className="flex items-center gap-2.5 px-5 py-3.5 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
                            <Icon icon="solar:link-circle-linear" className="h-4 w-4 text-slate-400" />
                            <span className="text-sm font-semibold text-slate-700">Upstream flow instances</span>
                        </div>
                        <div className="px-5 py-4 space-y-3">
                            <p className="text-xs text-slate-500">
                                Select instances from {validFlowForUpstream?.title ?? "valid flow"} to feed `relay-*` pins.
                            </p>
                            <Select
                                label="Connected upstream instances"
                                placeholder="Select upstream instances"
                                variant="bordered"
                                selectionMode="multiple"
                                selectedKeys={selectedUpstreamIds}
                                onSelectionChange={(keys) => {
                                    const nextIds = Array.from(keys).map((id) => `${id}`)
                                    syncUpstreamRelationships(nextIds)
                                }}
                            >
                                {upstreamCandidates.map((candidate) => (
                                    <SelectItem key={`${candidate.id}`}>
                                        {candidate.title || `Flow instance #${candidate.plainId ?? candidate.id}`}
                                    </SelectItem>
                                ))}
                            </Select>
                            {upstreamCandidates.length === 0 && (
                                <p className="text-xs text-slate-400">No instances available for the selected valid flow.</p>
                            )}
                        </div>
                    </div>
                )}
                <PrimitiveCard.InputPins
                    primitive={resolvedPrimitive}
                    pins={pinsToShow}
                    dataForNewInstance={isForNewInstance ? dataForNewInstance : undefined}
                    newInstanceCallback={isForNewInstance ? newInstanceCallback : undefined}
                    updateMissing={isForNewInstance ? setMissing : undefined}
                />
                {!isForNewInstance && flowStatus === "not_started" && (
                    <Button
                        className={`w-full ${accentButtonClassName}`}
                        onPress={() =>
                            MainStore().doPrimitiveAction(resolvedPrimitive, "continue_flow_instance", {
                                organizationId: MainStore().activeOrganization?.id,
                            })
                        }
                        startContent={<Icon icon="solar:play-circle-linear" className="h-5 w-5" />}
                    >
                        Run
                    </Button>
                )}
                {isForNewInstance && (
                    <div className="flex justify-end space-x-3">
                        {isEmbedded && (
                            <Button
                                variant="light"
                                onPress={() => {
                                    window.parent?.postMessage("close_newflow", "*");
                                }}
                            >
                                Cancel
                            </Button>
                        )}
                        <Button color="success" isDisabled={!enableSubmit} onPress={createNewInstance}>
                            Submit
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    const renderOverviewPanel = () => (
        <div className="flex h-full min-h-0 flex-col gap-4 w-full max-w-5xl mx-auto sm:w-3/5">
                <WorkflowInstanceStatus primitive={resolvedPrimitive} showControls={false} />
        </div>
    );

    const renderCompleteContent = () => {
        switch (completeView) {
            case "overview":
                return (
                    <div className="flex-1 min-h-0 overflow-y-auto px-0 sm:px-6 pb-6 mt-4 mb-6">
                        {renderOverviewPanel()}
                    </div>
                );
            case "output":
                {
                const inspectorPanel = renderInspectorPanel("w-full");
                const showInspectorPanel = Boolean(inspectorPanel);
                return (
                    <div className="w-full h-full flex gap-2">
                        <div className="flex w-full h-full min-h-0 gap-2 flex-col min-w-60">
                                <div className="flex-1 min-h-0 overflow-hidden ">
                                    <FlowInstanceOutput
                                        key={`flow-main-${resolvedPrimitive?.id ?? id ?? "none"}-${dataLoadVersion}`}
                                        ref={flowInfoRef}
                                        singleFrame={true}
                                        pagesOnly={true}
                                        deferBuildWhileLoading={true}
                                        isDataLoading={isWorkspaceLoading}
                                        dataLoadVersion={dataLoadVersion}
                                        hideEmptyPages={hideSlidesWithEmptyItems}
                                        background="inherit"
                                        disableHover={false}
                                        enableTextSelection={true}
                                        frameIdx={selectedPage}
                                        primitive={resolvedPrimitive}
                                        inputPrimitives={inputs}
                                        steps={stepsToProcess}
                                        select={(pOrId, options) => {
                                            const primitives = pOrId
                                                ? [pOrId]
                                                      .flat()
                                                      .map((item) =>
                                                          typeof item === "object" ? item : MainStore().primitive(item)
                                                      )
                                                      .filter(Boolean)
                                                : [];
                                            if (!primitives.length) {
                                                setInspectorFocus(undefined);
                                                return;
                                            }
                                            // VizProvenance shortcut: show evidence + sources directly
                                            if (options?.vizProvenance) {
                                                setInspectorFocus({ primitives, ...options, showExecution: false });
                                                return;
                                            }
                                            // Detect structured_summary on non-DSL primitives and
                                            // route to the vizProvenance inspector (same as DSL blocks)
                                            const primary = primitives[0];
                                            const summaryPrim = options?.summaryPrimitive;
                                            const summaryPrims = options?.summaryPrimitives?.length ? options.summaryPrimitives : [summaryPrim].filter(Boolean);
                                            const allSections = summaryPrims
                                                .flatMap(s => s?.referenceParameters?.structured_summary ?? []);
                                            const structuredSummary = allSections.length > 0 ? allSections
                                                : (primary?.referenceParameters?.structured_summary ?? []);
                                            if (!options?.vizProvenance && !options?.imageGallery && structuredSummary.length > 0) {
                                                const sectionConfig = primary?.referenceParameters?.sections ?? {};
                                                const visibleSections = structuredSummary.filter(s => {
                                                    if (!s.heading) return true;
                                                    if (sectionConfig[s.heading]?.show === false) return false;
                                                    // Skip bare "Title" sections when other sections have content
                                                    if (s.heading === "Title" && !s.quote?.length && !Object.keys(s.id_map || {}).length) {
                                                        const hasOthers = structuredSummary.some(o =>
                                                            o.heading !== "Title" &&
                                                            (o.quote?.length || Object.keys(o.id_map || {}).length)
                                                        );
                                                        if (hasOthers) return false;
                                                    }
                                                    return true;
                                                });
                                                const provenances = visibleSections.map(section => ({
                                                    key: section.heading || "",
                                                    evidence: section.content || "",
                                                    verified: true,
                                                    sourceIds: section.ids || [],
                                                    _idMap: section.id_map || {},
                                                    _quotes: section.quote || [],
                                                }));
                                                const sourceIds = [...new Set(visibleSections.flatMap(s => s.ids || []))];
                                                setInspectorFocus({
                                                    primitives,
                                                    vizProvenance: {
                                                        blockId: primary.title ?? primary.plainId,
                                                        provenances,
                                                        sourceIds,
                                                        summaryId: summaryPrim?.id ?? primary.id,
                                                    },
                                                    showExecution: false,
                                                });
                                                return;
                                            }
                                            const nextFocus = { primitives, ...options, showExecution: false };
                                            const isImageGallery = options?.imageGallery?.type === "image_gallery";
                                            if (isImageGallery) {
                                                setInspectorFocus({ primitives, ...options, showExecution: false });
                                                return;
                                            }
                                            const hasExplicitSourceLists =
                                                Array.isArray(options?.sourceList) || Array.isArray(options?.poolList);
                                            const shouldShowSources =
                                                !isImageGallery &&
                                                (hasExplicitSourceLists ||
                                                options?.plainData ||
                                                (!options?.asList &&
                                                    !options?.list &&
                                                    ["summary", "result", "query", "element"].includes(primitives[0]?.type)));
                                            if (shouldShowSources) {
                                                const sourceFocus = buildSourceFocus(primitives, options);
                                                const sourceItems = sourceFocus?.sourceItems ?? [];
                                                // Route through vizProvenance panel for consistent UI
                                                const fallbackList = sourceItems.length > 0 ? sourceItems
                                                    : sourceFocus?.availableSources?.length > 0 ? sourceFocus.availableSources
                                                    : null;
                                                setInspectorFocus({
                                                    primitives,
                                                    vizProvenance: {
                                                        blockId: primary.title ?? primary.plainId ?? "Sources",
                                                        provenances: [],
                                                        sourceList: fallbackList ?? undefined,
                                                        sourceIds: fallbackList ? undefined : (sourceFocus?.sourcePrimitiveIds ?? []),
                                                        summaryId: sourceFocus?.generator?.id ?? primary.id,
                                                    },
                                                    showExecution: false,
                                                });
                                                return;
                                            }
                                            if (options?.list?.length > 0) {
                                                setInspectorFocus(nextFocus);
                                                return;
                                            }
                                    }}
                                        hideProgressAt="@4xl"
                                    />
                                </div>
                                <div className="mx-4 mt-2 mb-1 flex justify-end">
                                    <TooggleButton
                                        title="Hide slides with empty items"
                                        enabled={hideSlidesWithEmptyItems}
                                        setEnabled={setHideSlidesWithEmptyItems}
                                    />
                                </div>
                                <div className="flex-1 h-32 max-h-32 overflow-hidden border-t border-slate-200 p-1 mx-4 mb-4">
                                    <FlowInstanceOutput
                                        key={`flow-gallery-${resolvedPrimitive?.id ?? id ?? "none"}-${dataLoadVersion}`}
                                        pagesOnly={true}
                                        deferBuildWhileLoading={true}
                                        isDataLoading={isWorkspaceLoading}
                                        dataLoadVersion={dataLoadVersion}
                                        enableZoom={false}
                                        hideEmptyPages={hideSlidesWithEmptyItems}
                                        background="inherit"
                                        ref={thumbnailsRef}
                                        enablePan="horizontal"
                                        enableFrameSelection="overlay"
                                        clickToSelect={(idx)=>{
                                            setSelectedPage(idx)
                                        }}
                                        accent={accentColor}
                                        primitive={resolvedPrimitive}
                                        highlightIdx={selectedPage}
                                        initialZoom="height_title"
                                        layout="horizontal"
                                        inputPrimitives={inputs}
                                        steps={stepsToProcess}
                                        select={(pOrId, options) => {
                                            console.log(pOrId)
                                        }}
                                    hideProgressAt="@4xl"
                                    />
                            </div>
                        </div>
                        {showInspectorPanel && (
                            <div className="flex min-h-0 w-[50rem] max-w-[50rem] flex-col gap-2">
                                {inspectorPanel}
                            </div>
                        )}
                    </div>
                );
                }
            case "inputs":
                return renderInputPanel();
            case "progress":
                return (
                    <div className="flex h-full min-h-0 gap-4 overflow-hidden">
                        <div className="flex-1 min-h-0 overflow-hidden rounded-2xl bg-white shadow-lg">
                            <WorkflowStructurePreview
                                statusMap={statusMap}
                                onClick={(itemIds, node) => {
                                    if (itemIds?.length) {
                                        const primitives = itemIds
                                            .map((pid) => (typeof pid === "object" ? pid : MainStore().primitive(pid)))
                                            .filter(Boolean);
                                        if (primitives.length > 0) {
                                            setInspectorFocus({
                                                primitives,
                                                nodeDetail: node,
                                                showExecution: false,
                                            });
                                        }
                                    }
                                    if (itemIds?.length) {
                                        MainStore().sidebarSelect(itemIds, { forFlow: true, flowInstance: resolvedPrimitive });
                                    }
                                }}
                            />
                        </div>
                        {renderInspectorPanel()}
                    </div>
                );
            case "timeline":
                return (
                    <div className="flex h-full min-h-0 overflow-auto rounded-2xl bg-white p-4 shadow-lg">
                        <GanttChart items={timelineData} width={1000} />
                    </div>
                );
            default:
                return null;
        }
    };

    if (viewState === "loading") {
        return (
            <div className="relative flex h-full flex-col overflow-hidden bg-gray-50">
                <div className="flex flex-1 items-center justify-center text-sm text-slate-400">Loading flow…</div>
                {isWorkspaceLoading && (
                    <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/45 backdrop-blur-sm">
                        <Spinner color="primary" label="Loading flow instance" />
                    </div>
                )}
            </div>
        );
    }

    if (viewState === "new") {
        return (
            <div className="relative flex h-full min-h-0 flex-col overflow-hidden bg-gray-50">
                {workspaceHeader()}
                <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-hidden px-6 pb-6 lg:flex-row mt-4">
                    <div className="flex-1 min-h-0 overflow-y-scroll">{renderInputPanel()}</div>
                </div>
                {isWorkspaceLoading && (
                    <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/45 backdrop-blur-sm">
                        <Spinner color="primary" label="Loading flow instance" />
                    </div>
                )}
            </div>
        );
    }

    if (viewState === "status") {
        return (
            <div className="relative flex h-full min-h-0 flex-col overflow-hidden bg-gray-50">
                {workspaceHeader()}
                <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-hidden px-0 sm:px-6 pb-6 overflow-y-scroll mt-4 mb-6">
                    {renderOverviewPanel()}
                </div>
                {isWorkspaceLoading && (
                    <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/45 backdrop-blur-sm">
                        <Spinner color="primary" label="Loading flow instance" />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="relative flex h-full min-h-0 flex-col overflow-hidden bg-gray-50">
            {workspaceHeader({
                items: completeNavItems,
                selectedKey: completeView,
                onSelectionChange: setCompleteView,
            })}
            <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-hidden @container">
                {renderCompleteContent()}
            </div>
            {isWorkspaceLoading && (
                <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/45 backdrop-blur-sm">
                    <Spinner color="primary" label="Loading flow instance" />
                </div>
            )}
        </div>
    );
}
